import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Shield } from 'lucide-react';
import { useGeneralRestrictions } from '@/hooks/useGeneralRestrictions';

const GeneralRestrictions: React.FC = () => {
  const navigate = useNavigate();
  const { restrictions, updateRestriction, isLoading } = useGeneralRestrictions();

  const handleRestrictionToggle = async (restrictionId: string, enabled: boolean) => {
    try {
      await updateRestriction(restrictionId, enabled);
    } catch (error) {
      console.error('Failed to update restriction:', error);
    }
  };

  const restrictionItems = [
    {
      id: 'block_gifs',
      title: 'Block GIFs',
      description: 'Prevent animated GIF images from loading',
      icon: '🎞️'
    },
    {
      id: 'block_ads',
      title: 'Block Advertisements',
      description: 'Block ads and promotional content',
      icon: '🚫'
    },
    {
      id: 'safe_search',
      title: 'Safe Search',
      description: 'Enable safe search on search engines',
      icon: '🔍'
    },
    {
      id: 'block_downloads',
      title: 'Block Downloads',
      description: 'Prevent file downloads',
      icon: '📥'
    },
    {
      id: 'block_incognito',
      title: 'Block Incognito Mode',
      description: 'Disable private browsing mode',
      icon: '🕵️'
    },
    {
      id: 'time_restrictions',
      title: 'Time Restrictions',
      description: 'Enable time-based access controls',
      icon: '⏰'
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">General Restrictions</h1>
            <p className="text-muted-foreground">Configure general content and behavior restrictions</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Content & Behavior Controls
            </CardTitle>
            <CardDescription>
              Enable or disable various restrictions to control device behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Loading restrictions...</p>
              </div>
            ) : (
              restrictionItems.map((item) => {
                const isEnabled = restrictions?.[item.id] || false;
                return (
                  <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{item.icon}</div>
                      <div>
                        <h3 className="font-medium text-foreground">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(checked) => handleRestrictionToggle(item.id, checked)}
                    />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GeneralRestrictions;